import { useEffect, useState, type ReactElement } from 'react';
import { api, type User } from '../api';

/**
 * The page behind an invitation or a reset link. Both do the same thing --
 * choose a password and end up signed in -- so they are one screen with two
 * headings rather than two screens that drift apart.
 */
export function Einladung({
  kind,
  token,
  onSignedIn,
}: {
  kind: 'einladung' | 'passwort';
  token: string;
  onSignedIn: (user: User) => void;
}): ReactElement {
  const [state, setState] = useState<{ email: string; name?: string } | null>(null);
  const [gone, setGone] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = kind === 'einladung' ? api.invitation(token) : api.resetToken(token);
    void load
      .then((found) => {
        setState(found);
        if ('name' in found && typeof found.name === 'string' && found.name) setName(found.name);
      })
      .catch((err) => setGone(err instanceof Error ? err.message : 'Dieser Link ist nicht mehr gültig.'));
  }, [kind, token]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { user } =
        kind === 'einladung'
          ? await api.acceptInvitation(token, { name: name.trim(), password })
          : await api.resetPassword(token, password);
      onSignedIn(user);
      history.replaceState(null, '', '/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Das hat nicht geklappt.');
    } finally {
      setBusy(false);
    }
  };

  if (gone) {
    return (
      <div className="page">
        <h1>Schnitt</h1>
        <p className="error-text">{gone}</p>
        <p className="hint">
          {kind === 'einladung'
            ? 'Bitte um eine neue Einladung.'
            : 'Fordere auf der Anmeldeseite einen neuen Link an.'}
        </p>
        <button className="primary" style={{ width: '100%' }} onClick={() => (location.href = '/')}>
          Zur Anmeldung
        </button>
      </div>
    );
  }
  if (!state) return <div className="page laedt">Einen Moment…</div>;

  return (
    <div className="page">
      <h1>Schnitt</h1>
      <p className="lede">
        {kind === 'einladung' ? 'Willkommen. Vergib ein Passwort.' : 'Neues Passwort vergeben.'}
      </p>
      <form className="card" onSubmit={submit}>
        <label className="field">
          <span>E-Mail</span>
          <input value={state.email} readOnly />
        </label>
        {kind === 'einladung' ? (
          <label className="field">
            <span>Dein Name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
          </label>
        ) : null}
        <label className="field">
          <span>Passwort</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            minLength={10}
            required
            autoFocus={kind === 'passwort'}
          />
          <p className="hint">Mindestens zehn Zeichen.</p>
        </label>
        {error ? <p className="error-text">{error}</p> : null}
        <button className="primary" type="submit" disabled={busy} style={{ width: '100%' }}>
          {busy ? 'Einen Moment…' : kind === 'einladung' ? 'Zugang einrichten' : 'Passwort speichern'}
        </button>
      </form>
    </div>
  );
}
